import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';

const PricingPage = ({ onLoginRequest }) => {
  const { user } = useAuth();
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' or 'yearly'

  const createCheckoutSession = async (priceId, isTrial = false) => {
    if (!user || user.isAnonymous) {
      if (onLoginRequest) {
        onLoginRequest(() => createCheckoutSession(priceId, isTrial));
      }
      return;
    }

    try {
      const idToken = await user.getIdToken();
      const isDevelopment = process.env.NODE_ENV === 'development';
      const functionUrl = isDevelopment
        ? 'http://127.0.0.1:5001/sabr-project-final/us-central1/createCheckoutSession'
        : 'https://us-central1-sabr-project-final.cloudfunctions.net/createCheckoutSession';

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({ data: { price: priceId, isTrial: isTrial } })
      });

      const result = await response.json();

      if (response.ok && result?.data?.url) {
        window.location.href = result.data.url;
      } else {
        throw new Error(result?.error?.message || 'Failed to create checkout session.');
      }
    } catch (error) {
      console.error("Error creating checkout session:", error);
      alert(`Could not create a checkout session: ${error.message}`);
    }
  };

  const monthlyPriceId = 'price_1S4e7TGRpQq3tf520y0Fvveb';
  const yearlyPriceId = 'price_1S4ejcGRpQq3tf52Ej1hyhDw';

  return (
    <div className="p-4 md:p-8">
      <div className="text-center">
        <h1 className="text-4xl font-bold mb-4">Choose Your Plan</h1>
        <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-2xl mx-auto">
          Unlock the full potential of SABRCalculator with a Pro subscription, or continue with our generous free tier.
        </p>

        <div className="flex justify-center items-center space-x-4 mb-10">
          <span className={`font-medium ${billingCycle === 'monthly' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500'}`}>
            Monthly
          </span>
          <label htmlFor="billing-toggle" className="relative inline-flex items-center cursor-pointer">
            <input 
              type="checkbox" 
              id="billing-toggle" 
              className="sr-only peer"
              checked={billingCycle === 'yearly'}
              onChange={() => setBillingCycle(prev => prev === 'monthly' ? 'yearly' : 'monthly')}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
          </label>
          <span className={`font-medium ${billingCycle === 'yearly' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500'}`}>
            Yearly
          </span>
          <span className="bg-green-100 text-green-800 text-xs font-semibold ml-2 px-2.5 py-0.5 rounded-full dark:bg-green-900 dark:text-green-300">
            Save 50%
          </span>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
        
        {/* Free Tier Card */}
        <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-6 flex flex-col">
          <h2 className="text-2xl font-semibold">Free Tier</h2>
          <p className="text-4xl font-bold my-4">$0 <span className="text-lg font-normal">/ month</span></p>
          <ul className="space-y-2 flex-grow">
            <li>✅ RTOG & Advanced Calculators</li>
            <li>✅ SABR Education Modules</li>
            <li>✅ 5 most recent Article Summaries</li>
            <li>✅ Latest Monthly Trend Report</li>
          </ul>
        </div>

        {/* Pro Tier Card */}
        <div className="border-2 border-blue-600 rounded-lg p-6 relative flex flex-col">
          <span className="bg-blue-600 text-white px-3 py-1 text-sm font-semibold rounded-full absolute -top-4 left-1/2 -translate-x-1/2">
            Most Popular
          </span>
          <h2 className="text-2xl font-semibold">Pro Tier</h2>

          {billingCycle === 'monthly' ? (
            <p className="text-4xl font-bold my-4">$10 <span className="text-lg font-normal">/ month</span></p>
          ) : (
            <p className="text-4xl font-bold my-4">$60 <span className="text-lg font-normal">/ year</span></p>
          )}

          <ul className="space-y-2 mb-6 flex-grow">
            <li>✅ <strong>Everything in Free, plus:</strong></li>
            <li>✅ AI CPD Assistant (Time Saver!)</li>
            <li>✅ Unlimited Article Summaries & Archive</li>
            <li>✅ CPD Log Dashboard & PDF Export</li>
            <li>✅ Premium Monthly Email Digest</li>
          </ul>
          
          <div className="flex flex-col space-y-3">
            {billingCycle === 'monthly' ? (
              <button
                onClick={() => createCheckoutSession(monthlyPriceId, true)}
                className="w-full bg-indigo-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
              >
                Start 1-Month Free Trial
              </button>
            ) : (
              <button
                onClick={() => createCheckoutSession(yearlyPriceId)}
                className="w-full bg-green-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-green-700 transition-colors"
              >
                Subscribe Yearly & Save 50%
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;

